import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertCircle, Copy, ImageIcon, Inbox, Loader2, Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useJobDetailQuery, useJobsQuery } from "@/lib/queries";
import { toast } from "@/stores/ui";
import type { JobDetail, JobListItem } from "@/lib/types";
import { jobTypeLabel, statusLabel, statusVariant, formatTime, isJobInFlight } from "@/lib/format";
import { ResultCard } from "@/components/shared/ResultCard";
import { cn } from "@/lib/utils";

export function JobsView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedFromUrl = searchParams.get("selected");
  const [selectedId, setSelectedId] = useState<string | null>(selectedFromUrl);

  // sync URL param to local state on first load
  useEffect(() => {
    if (selectedFromUrl) setSelectedId(selectedFromUrl);
  }, [selectedFromUrl]);

  const { items } = useJobsQuery({ refetchInterval: 5_000 });

  // Auto-select first job if none selected
  useEffect(() => {
    if (!selectedId && items.length > 0) {
      setSelectedId(items[0].job_id);
    }
  }, [items, selectedId]);

  const detailQuery = useJobDetailQuery(selectedId, {
    refetchInterval: (query) => {
      const data = query.state.data as JobDetail | undefined;
      if (!data) return false;
      // Poll while the backend still owns the job. The state names must match
      // the backend exactly — see JobStatus in lib/types.ts.
      return isJobInFlight(data.status) ? 2_000 : false;
    },
  });

  const handleSelect = (id: string) => {
    setSelectedId(id);
    setSearchParams({ selected: id });
  };

  const detail = detailQuery.data;
  const originalPrompt = getOriginalPrompt(detail);

  const handleCopyPrompt = async () => {
    if (!originalPrompt) {
      toast("这条任务没有可复制的原始提示词");
      return;
    }
    try {
      await copyText(originalPrompt);
      toast("提示词已复制");
    } catch {
      toast("复制失败，请手动复制");
    }
  };

  return (
    <div className="grid grid-cols-[360px_1fr] gap-5 max-[1100px]:grid-cols-1">
      <section className="flex max-h-[calc(100vh-140px)] flex-col rounded-lg border border-border bg-card">
        <header className="border-b border-border px-5 py-4">
          <h3 className="text-sm font-semibold tracking-tight">任务列表</h3>
        </header>
        <div className="flex flex-col gap-1 overflow-y-auto p-2">
          {items.length === 0 ? (
            <EmptyState
              icon={<Inbox className="h-7 w-7" />}
              title="暂无任务"
              hint="提交一条生成任务后，会出现在这里。"
            />
          ) : (
            items.map((job) => (
              <JobItem
                key={job.job_id}
                job={job}
                active={job.job_id === selectedId}
                onClick={() => handleSelect(job.job_id)}
              />
            ))
          )}
        </div>
      </section>

      <section className="flex max-h-[calc(100vh-140px)] flex-col rounded-lg border border-border bg-card">
        <header className="flex items-center justify-between border-b border-border px-5 py-4">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-semibold tracking-tight">
              {detail ? `#${detail.job_id}` : "暂无任务"}
            </h3>
            {detail && (
              <Badge variant={statusVariant(detail.status)}>
                {statusLabel(detail.status)}
              </Badge>
            )}
          </div>
          {detail && (
            <Button
              variant="ghost"
              size="sm"
              disabled={!originalPrompt}
              onClick={handleCopyPrompt}
            >
              <Copy className="h-3.5 w-3.5" />
              复制提示词
            </Button>
          )}
        </header>

        <div className="flex-1 overflow-y-auto p-5">
          {!detail ? (
            <EmptyState
              icon={<Search className="h-7 w-7" />}
              title="先选一条任务"
              hint="在左侧点一个任务，结果图、失败原因或生成进度会显示在这里。"
            />
          ) : (
            <div className="flex flex-col gap-5">
              <DetailMeta job={detail} />
              {detail.error_message && (
                <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                  {detail.error_message}
                </div>
              )}
              {detail.result_assets.length > 0 ? (
                <div
                  className={cn(
                    "grid gap-4",
                    detail.result_assets.length === 1
                      ? "grid-cols-1 justify-items-center"
                      : "grid-cols-1 md:grid-cols-2",
                  )}
                >
                  {detail.result_assets.map((asset) => (
                    <ResultCard key={asset.asset_id} asset={asset} />
                  ))}
                </div>
              ) : isJobInFlight(detail.status) ? (
                <EmptyState
                  icon={<Loader2 className="h-7 w-7 animate-spin" />}
                  title="任务生成中"
                  hint="AI 模型出图通常需要 1-5 分钟，页面会自动刷新。你可以切到其他模块继续操作，任务在后台继续。"
                />
              ) : detail.error_message ? (
                <EmptyState
                  icon={<AlertCircle className="h-7 w-7" />}
                  title="任务失败"
                  hint={detail.error_message}
                />
              ) : (
                <EmptyState
                  icon={<ImageIcon className="h-7 w-7" />}
                  title="暂无结果"
                  hint="该任务已结束，但未返回结果图。"
                />
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function EmptyState({
  icon,
  title,
  hint,
}: {
  icon: React.ReactNode;
  title: string;
  hint: string;
}) {
  return (
    <div className="flex h-full min-h-[260px] flex-col items-center justify-center gap-3 px-6 text-center">
      <div className="grid h-16 w-16 place-items-center rounded-full border border-border bg-card text-muted-foreground">
        {icon}
      </div>
      <p className="text-base font-medium text-foreground">{title}</p>
      <p
        className="max-w-[360px] text-sm leading-relaxed text-muted-foreground"
        style={{ textWrap: "balance" }}
      >
        {hint}
      </p>
    </div>
  );
}

function JobItem({
  job,
  active,
  onClick,
}: {
  job: JobListItem;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "grid w-full grid-cols-[56px_1fr] items-center gap-3 rounded-md border bg-input p-3 text-left transition-all duration-150 hover:-translate-y-px hover:border-border/80",
        active ? "border-accent bg-accent/14" : "border-transparent"
      )}
    >
      {(() => {
        const thumb = job.source_asset.thumbnail_url || job.first_thumbnail;
        return thumb ? (
          <img
            src={thumb}
            alt=""
            loading="lazy"
            className="aspect-square w-14 rounded-sm bg-card object-cover"
            onError={(e) => {
              // Broken thumb: just hide the img, the rest of the row still
              // shows status + cost so the user can still inspect the job.
              (e.currentTarget as HTMLElement).style.visibility = "hidden";
            }}
          />
        ) : (
          <div className="aspect-square w-14 rounded-sm bg-card" />
        );
      })()}
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex items-center justify-between gap-2">
          <strong className="truncate text-sm font-semibold text-foreground">
            {jobTypeLabel(job.job_type)}
          </strong>
          <Badge variant={statusVariant(job.status)}>
            {statusLabel(job.status)}
          </Badge>
        </div>
        <span className="text-xs text-muted-foreground">
          {formatTime(job.created_at)} · {job.cost_credits} 点 · {job.result_count} 张
        </span>
        {isJobInFlight(job.status) && (
          <div className="h-1 overflow-hidden rounded-full bg-card">
            <div
              className="h-full bg-accent transition-all"
              style={{ width: `${Math.max(0, Math.min(100, job.progress ?? 0))}%` }}
            />
          </div>
        )}
      </div>
    </button>
  );
}

function DetailMeta({ job }: { job: JobDetail }) {
  const rows: [string, string][] = [
    ["任务类型", jobTypeLabel(job.job_type)],
    ["创建", formatTime(job.created_at)],
    ["完成", job.finished_at ? formatTime(job.finished_at) : "—"],
    ["扣费", `${job.cost_credits} 点`],
    ["进度", `${job.progress}%`],
  ];
  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-2 rounded-md border border-border bg-input px-4 py-3 sm:grid-cols-3">
      {rows.map(([k, v]) => (
        <div key={k} className="flex flex-col gap-0.5">
          <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">{k}</dt>
          <dd className="font-mono text-sm tabular-nums text-foreground">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function getOriginalPrompt(job?: JobDetail): string {
  if (!job) return "";
  const fromConfig = job.config?.original_prompt;
  if (typeof job.original_prompt === "string" && job.original_prompt.trim()) {
    return job.original_prompt;
  }
  if (typeof fromConfig === "string" && fromConfig.trim()) return fromConfig;
  return typeof job.prompt === "string" ? job.prompt : "";
}

async function copyText(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
}
